import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	AutorizedSpecialite,
	FichierPrerequisSollicitationWithContent,
	LinksPreferencesSollicitation,
	PreferencesSollicitation,
	TypeFormulaireSollicitation,
	TypeSollicitation,
} from "../../../core/apis/monsisra/generated";
import { ProfessionOPFilter } from "../../../core/apis/monsisra/generated.v2";
import { fetchProfessionsAndSpecialites, getPreferences, getTypesFormulaire, savePreferences } from "./expertise.action";
import { UtilsService } from "../../../core/services/utils.service";

export interface ExpertiseState {
	type: TypeSollicitation;
	preferences?: PreferencesSollicitation;
	professionsAndSpecialites?: ProfessionOPFilter[];
	formulaires: TypeFormulaireSollicitation[];
	links: LinksPreferencesSollicitation;
	useMonSisra: boolean;
}

const initialState: ExpertiseState = {
	type: new UtilsService().getDefaultType(),
	formulaires: [],
	links: {
		formulairesSollicitation: "",
		infosFacturation: "",
		guideTeleexpertise: "",
		numeroFacturation: "",
		demanderNumeroFacturation: "",
		creationSollicitation: "",
		finalisationTeleexpertise: "",
	},
	useMonSisra: true,
};
type DeepPartial<T> = {
	[P in keyof T]?: T[P] extends object | undefined ? DeepPartial<T[P]> | undefined : T[P];
};

const slice = createSlice({
	name: "expertise",
	initialState,
	reducers: {
		setType: (state, { payload }: PayloadAction<ExpertiseState["type"]>) => {
			state.type = payload;
		},
		removeFile: (state, { payload }: PayloadAction<FichierPrerequisSollicitationWithContent["fileName"]>) => {
			state.preferences!.prerequis.files = state.preferences!.prerequis.files.filter((f) => f.fileName !== payload);
		},
		setPreferencesPartial: (state, { payload }: PayloadAction<DeepPartial<PreferencesSollicitation>>) => {
			state.preferences = new UtilsService().deepMerge(state.preferences, payload);

			const restriction = payload.preferencesRestriction;
			if (restriction?.autorizedProfessionIds !== undefined) {
				state.preferences!.preferencesRestriction!.autorizedProfessionIds = restriction.autorizedProfessionIds as number[];
			}
			if (restriction?.autorizedSpecialites !== undefined) {
				state.preferences!.preferencesRestriction!.autorizedSpecialites = restriction.autorizedSpecialites as AutorizedSpecialite[];
			}
		},
		setUseMonSisra: (state, { payload }: PayloadAction<ExpertiseState["useMonSisra"]>) => {
			state.useMonSisra = payload;
		},
	},
	extraReducers: ({ addCase }) => {
		function clearIdTypeFormulaire(state: ExpertiseState) {
			if (state.preferences && !state.formulaires.some((type) => type.idTypeFormulaire === state.preferences?.idTypeFormulaire)) {
				state.preferences.idTypeFormulaire = undefined;
			}
		}
		addCase(getPreferences.fulfilled, (state, action) => {
			state.preferences = action.payload;
			state.useMonSisra = !action.payload.urlOutil;
			clearIdTypeFormulaire(state);
		});
		addCase(savePreferences.fulfilled, (state, action) => {
			state.preferences = action.payload;
		});
		addCase(getTypesFormulaire.fulfilled, (state, action) => {
			state.formulaires = action.payload.typesFormulaireSollicitation;
			state.links = action.payload.linksPreferencesSollicitation;
			clearIdTypeFormulaire(state);
		});
		addCase(fetchProfessionsAndSpecialites.fulfilled, (state, action) => {
			const professionsPs = action.payload?.professionPsFilters ?? [];
			const professionsUo = action.payload?.professionUoFilters ?? [];
			state.professionsAndSpecialites = [...professionsPs, ...professionsUo];
		});
	},
});

export const {
	reducer: expertiseReducer,
	actions: { setType, setPreferencesPartial, setUseMonSisra, removeFile },
} = slice;
